import { Text, Tooltip } from '@mantine/core';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useMemo } from 'react';
import { formatGivenFamilyName } from '@bariendo/lib/human-name';
import { PostConsultationLeadRow } from '../services/postConsultationRows';
import { PeopleCell, PersonCell } from './PersonCell';
import { formatListDate, lastCommunicationColumn, nextTaskColumn } from './sharedColumns';

/**
 * Columns for the post-consultation queue.
 *
 * Every accessor tolerates a missing row — see the header of
 * `preConsultationColumns.tsx` for why. Short version: mantine-react-table calls
 * `accessorFn` with nothing when the table has zero rows, and an accessor that
 * assumes its row exists takes the whole tab to the error boundary.
 */

const QUICK_NOTE_WIDTH = 230;

export function usePostConsultationColumns(now: Date): MRT_ColumnDef<PostConsultationLeadRow>[] {
  return useMemo(
    () => [
      {
        id: 'firstName',
        header: 'First name',
        accessorFn: (row) => row?.patient?.name?.[0]?.given?.join(' ') ?? '',
      },
      {
        id: 'lastName',
        header: 'Last name',
        accessorFn: (row) => row?.patient?.name?.[0]?.family ?? '',
      },
      {
        id: 'location',
        header: 'Location',
        accessorFn: (row) => row?.location ?? '—',
      },
      {
        id: 'consultDate',
        header: 'Consult date',
        accessorFn: (row) => row?.consult?.start ?? '',
        sortingFn: 'datetime',
        Cell: ({ row }) => <PersonCell text={formatListDate(row.original.consult?.start)} name={row.original.np} />,
      },
      {
        id: 'consultStatus',
        header: 'Consult status',
        // The status of the SAME appointment the Consult date shows — never a
        // different consult's.
        accessorFn: (row) => row?.consult?.status ?? '—',
      },
      {
        id: 'patientNavigator',
        header: 'Patient navigator',
        accessorFn: (row) => (row?.patientNavigators ?? []).map(formatGivenFamilyName).join(', '),
        enableSorting: false,
        // Every PN on the care team, not just the assigned one: the column answers
        // "who does this patient already know", and a second navigator who has
        // spoken to them is part of that answer.
        Cell: ({ row }) => <PeopleCell names={row.original.patientNavigators} />,
      },
      {
        id: 'nextStep',
        header: 'Next step',
        // An NP consult's next-step answers as "first + N" (the full set is on the
        // patient page — wrapping would break the row rhythm for a minority of
        // rows), or a PN consultation's saved Outcome. Sorting a multi-value
        // column is ambiguous by nature, a second reason the sort lives on
        // consult date.
        accessorFn: (row) => row?.nextStep ?? '—',
        enableSorting: false,
      },
      {
        id: 'quickNote',
        header: 'Quick note to PN',
        accessorFn: (row) => row?.quickNote ?? '',
        size: QUICK_NOTE_WIDTH,
        enableSorting: false,
        // The only free-text column, and the one a rep actually reads — it is the
        // NP telling them what to say. Fixed width, truncated, full text on hover.
        // An empty note renders an em-dash, never blank: a blank cell in a text
        // column reads as a rendering fault.
        Cell: ({ row }) => {
          const note = row.original.quickNote;

          if (!note) {
            return '—';
          }

          return (
            <Tooltip label={note} multiline w={360} withArrow position="top-start">
              <Text
                component="span"
                style={{
                  display: 'block',
                  maxWidth: QUICK_NOTE_WIDTH,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {note}
              </Text>
            </Tooltip>
          );
        },
      },
      lastCommunicationColumn<PostConsultationLeadRow>(),
      nextTaskColumn<PostConsultationLeadRow>(now),
    ],
    [now]
  );
}
